import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Select,
  ListSubheader,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  FormHelperText,
} from '@nextiva/next-ui-components';
import SearchIcon from '@material-ui/icons/Search';
import HighlightOff from '@material-ui/icons/HighlightOff';
import clsx from 'clsx';
import { formatToPhoneNumber } from '../../utils/phone-number';

export const SelectSearchInput = ({
  displayOptions,
  onChange,
  field,
  field: { value },
  error,
  'data-testid': dataTestId,
}) => {
  const [searchText, setSearchText] = useState('');
  const onSelectChange = (event) => {
    onChange(event.target.value?.replace(/\D/g, ''));
  };

  const onRemoveButtonClick = () => {
    onChange('');
  };

  const searchTextOnlyNumbers = searchText.replace(/\D/g, '');

  const formattedFilteredOptions = [];

  displayOptions.forEach((displayOption, index) => {
    if (
      displayOption.value &&
      displayOption.value.startsWith(searchTextOnlyNumbers)
    ) {
      formattedFilteredOptions.push(
        <MenuItem
          key={index}
          value={displayOption.label}
          className="m-select-search-input__display-options"
          data-testid={`PlatformAdminUI-SelectSearchInput-MenuList-${displayOption.label}`}
        >
          {displayOption.label}
        </MenuItem>,
      );
    }
  });

  return (
    <div
      className={clsx(
        'm-select-search-input',
        value && 'm-select-search-input-hide-dropdown',
      )}
      data-testid="PlatformAdminUI-SelectSearchInput-SearchField"
    >
      <FormControl fullWidth error={!!error}>
        <Select
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            PaperProps: {
              style: {
                maxHeight: '236px',
                width: 235,
                marginTop: 3,
              },
            },
            getContentAnchorEl: null,
          }}
          {...field}
          renderValue={() => formatToPhoneNumber(value)}
          onChange={onSelectChange}
          onClose={() => setSearchText('')}
          className="m-select-search-input__test"
          data-testid={dataTestId}
        >
          <div className="m-select-search-input__search-bar-container">
            <ListSubheader>
              <TextField
                size="small"
                autoFocus
                placeholder="Search"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(event) => setSearchText(event.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') {
                    e.stopPropagation();
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                data-testid="PlatformAdminUI-SelectSearchInput-SearchText"
              />
            </ListSubheader>
          </div>
          {formattedFilteredOptions.length === 0 && (
            <div
              className="m-select-search-input__no-matches-text"
              data-testid="PlatformAdminUI-SelectSearchInput-NoMatches"
            >
              No matches
            </div>
          )}
          {formattedFilteredOptions}
        </Select>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
      {value && (
        <div
          onClick={onRemoveButtonClick}
          aria-hidden="true"
          className="m-select-search-input__clear-icon"
          data-testid="PlatformAdminUI-SelectSearchInput-SearchClearIcon"
        >
          <HighlightOff />
        </div>
      )}
    </div>
  );
};

SelectSearchInput.propTypes = {
  /* List of options to display within the dropdown menu */
  displayOptions: PropTypes.arrayOf(PropTypes.object),
  error: PropTypes.string,
  /* The field object from Controller */
  field: PropTypes.any,
  /* Callback function executed when selecting an option from the dropdown */
  onChange: PropTypes.func,
};

SelectSearchInput.defaultProps = {
  displayOptions: [],
  error: '',
  onChange: () => {},
};
